import { type ThemeConfig, theme as antdTheme } from 'antd';
import type { MapToken, SeedToken } from 'antd/es/theme/interface';

import { type HorizonTheme, Theme } from './horizonTheme';

// Controls and tables keep antd 4's 2px corners (decided 2026-10-21, docs/antd5-migration-plan.md
// 9.1).
const ANTD4_RADIUS = { borderRadius: 2, borderRadiusLG: 2, borderRadiusSM: 2 };

// The dark algorithm dims the brand and status colors for dark surfaces. The Horizon dark theme
// already picks dark-legible ones, so antd keeps them as given.
const keepHorizonBrandColors = (seedToken: SeedToken, mapToken?: MapToken): MapToken => ({
  ...(mapToken as MapToken),
  colorPrimary: seedToken.colorPrimary,
  colorLink: seedToken.colorLink,
  colorInfo: seedToken.colorInfo,
  colorSuccess: seedToken.colorSuccess,
  colorWarning: seedToken.colorWarning,
  colorError: seedToken.colorError,
});

export function getAntdThemeConfig(theme: Omit<HorizonTheme, 'onChange'>): ThemeConfig {
  const isDark = theme.themeName === Theme.Dark;
  // Inputs, selects and pickers share the app's input border and disabled fill.
  const inputControl = {
    ...ANTD4_RADIUS,
    colorBorder: theme.buttonBorderColor,
    colorBgContainerDisabled: theme.disabledBackground,
  };
  // Select and picker arrows, suffix icons and clear buttons, which antd draws in the faint
  // disabled-text color.
  const inputIcons = { colorTextQuaternary: theme.labelColor };

  return {
    algorithm: isDark
      ? [antdTheme.darkAlgorithm, keepHorizonBrandColors]
      : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: theme.teal,
      // Links use the deeper, legible action-text color rather than the
      // bright Sky Blue fill color, so inline text stays readable on light.
      colorLink: theme.actionText,
      colorLinkHover: theme.actionTextHover,
      colorInfo: theme.teal,
      colorSuccess: theme.green,
      colorWarning: theme.yellow,
      colorError: theme.red,
      // antd's status text is the fill color in light and a dimmed one in dark. Horizon's
      // redText is the red it picks for small text.
      colorErrorText: theme.redText,
      colorSuccessText: theme.green,
      colorWarningText: theme.yellow,
      fontFamily: 'Inter, sans-serif',
      borderRadius: 8,
      // antd's text steps are translucent black or white; Horizon's are solid navy-scale
      // colors. Placeholder, disabled, icon and label colors derive from these.
      colorTextBase: theme.textColor,
      colorText: theme.textColor,
      colorTextHeading: theme.headerColor,
      colorTextSecondary: theme.secondaryTextColor,
      colorTextTertiary: theme.descriptionColor,
      colorTextQuaternary: theme.disabledTextColor,
      // antd derives placeholders from the disabled step, under 2:1 on white.
      colorTextPlaceholder: theme.labelColor,
      colorBgSpotlight: theme.tooltipBackground,
      // antd's dark palette is neutral black and gray. The dark theme's surfaces and borders
      // are Horizon navy, and the algorithm derives its fills and hover states from them.
      ...(isDark && {
        colorBgBase: theme.primaryBackgroundColor,
        colorBgContainer: theme.secondaryBackgroundColor,
        colorBgElevated: theme.tertiaryBackgroundColor,
        colorBorder: theme.borderColor,
        colorBorderSecondary: theme.borderColor,
      }),
    },
    components: {
      Button: {
        ...ANTD4_RADIUS,
        // antd 4's icon-only sizes; antd 5 inherits the label size.
        onlyIconSize: 16,
        onlyIconSizeLG: 18,
        onlyIconSizeSM: 14,
        // Solid primary buttons use navy text on the Sky Blue fill so labels stay legible.
        primaryColor: theme.solidActionText,
        colorPrimaryHover: theme.tealActive,
        // Outlined buttons, danger and primary ones included, sit on the app's button fill.
        colorBgContainer: theme.buttonBackgroundColor,
        defaultBg: theme.buttonBackgroundColor,
        defaultBorderColor: theme.buttonBorderColor,
        defaultHoverBg: theme.secondaryBackgroundColor,
        defaultActiveBg: theme.secondaryBackgroundColor,
        colorBgContainerDisabled: theme.disabledBackground,
        borderColorDisabled: theme.disabledTextColor,
      },
      // Dark status banners use Horizon's tinted fills rather than antd's near-black ones.
      Alert: isDark
        ? {
            colorInfoBg: theme.tealBackgroundColor,
            colorInfoBorder: theme.lightBlue,
            colorWarningBg: theme.tableBannerBackgroundColor,
            colorErrorBg: theme.lightRed,
            colorSuccessBg: theme.green,
          }
        : {},
      // The current page reads as the page title; links light up without antd's hover chip.
      Breadcrumb: {
        lastItemColor: theme.headerColor,
        linkHoverColor: theme.teal,
        colorBgTextHover: 'transparent',
      },
      // Cards and Collapse panels are square, with Horizon's tertiary header strip.
      Card: {
        borderRadiusLG: 0,
        headerBg: theme.tertiaryBackgroundColor,
        colorBorderSecondary: theme.borderColor,
        fontWeightStrong: 400,
      },
      // Checkboxes and radios take the app's input fill and border.
      Checkbox: {
        ...ANTD4_RADIUS,
        colorBorder: theme.buttonBorderColor,
        colorBgContainer: theme.checkboxBackgroundColor,
        colorBgContainerDisabled: theme.checkboxBackgroundColor,
      },
      Collapse: {
        borderRadiusLG: 0,
        headerBg: theme.tertiaryBackgroundColor,
        colorBorder: theme.borderColor,
      },
      // Hovered, selected and in-range cells match the app's list selection rather than antd's
      // primary tint.
      DatePicker: {
        ...inputControl,
        ...inputIcons,
        cellHoverBg: theme.selectedListItemBackground,
        controlItemBgActive: theme.selectedListItemBackground,
      },
      // The only Sider is the SideNav's navy rail.
      Layout: {
        bodyBg: theme.primaryBackgroundColor,
        headerBg: theme.primaryBackgroundColor,
        footerBg: theme.primaryBackgroundColor,
        siderBg: theme.sidebarBg,
        triggerBg: theme.sidebarBorder,
        triggerColor: theme.sidebarText,
      },
      // Square menus with the app's menu highlight and list selection.
      Dropdown: {
        borderRadiusLG: 0,
        controlItemBgHover: theme.activeMenuItemBackgroundColor,
        controlItemBgActive: theme.selectedListItemBackground,
      },
      Input: inputControl,
      InputNumber: inputControl,
      // Menus render inside the app's square dropdowns, so they drop antd's rounded rows and
      // the vertical menu's right border.
      Menu: {
        activeBarBorderWidth: 0,
        borderRadiusLG: 0,
        itemBorderRadius: 4,
        subMenuItemBorderRadius: 4,
        itemHoverBg: theme.activeMenuItemBackgroundColor,
        itemActiveBg: theme.activeMenuItemBackgroundColor,
        ...(isDark && {
          itemBg: theme.tertiaryBackgroundColor,
          subMenuItemBg: theme.tertiaryBackgroundColor,
        }),
        // The SideNav is the only theme="dark" Menu; it sits on the navy rail in both themes.
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemColor: theme.sidebarText,
        darkItemHoverColor: theme.sidebarText,
        darkItemHoverBg: theme.sidebarActiveBg,
        darkItemSelectedColor: theme.mapControlsTextColor,
        darkItemSelectedBg: theme.sidebarActiveBg,
        darkItemDisabledColor: theme.sidebarTextMuted,
      },
      // Wireframe is antd's divided layout: header and footer rules, padding on each part
      // instead of the whole dialog.
      Modal: {
        borderRadiusLG: 0,
        colorSplit: theme.borderColor,
        fontWeightStrong: 500,
        wireframe: true,
      },
      // Pages sit on the button fill; the active one is outlined in the action color.
      Pagination: {
        colorPrimary: theme.actionText,
        colorPrimaryHover: theme.actionTextHover,
        itemActiveColor: theme.actionText,
        itemActiveColorHover: theme.actionTextHover,
        itemBg: theme.buttonBackgroundColor,
        itemActiveBg: theme.buttonBackgroundColor,
      },
      // antd's translucent track disappears on dark surfaces.
      Progress: isDark ? { remainingColor: theme.borderColor } : {},
      // Square popovers and popconfirms on the Horizon popover surface.
      Popover: {
        borderRadiusLG: 0,
        colorBgElevated: theme.popoverBackground,
      },
      // Radios match Checkbox. Button-style groups match the components/ui Button, with navy on
      // the solid fill as primary buttons use.
      Radio: {
        ...ANTD4_RADIUS,
        colorBorder: theme.buttonBorderColor,
        colorBgContainer: theme.checkboxBackgroundColor,
        colorBgContainerDisabled: theme.checkboxBackgroundColor,
        colorPrimaryHover: theme.tealActive,
        buttonBg: theme.buttonBackgroundColor,
        buttonCheckedBg: theme.buttonBackgroundColor,
        buttonSolidCheckedColor: theme.solidActionText,
        buttonSolidCheckedHoverBg: theme.tealActive,
      },
      // The dark track and selection match the app's navy inputs and list selection, which
      // antd's derived layout and elevated surfaces do not.
      Segmented: isDark
        ? {
            trackBg: theme.secondaryBackgroundColor,
            itemHoverBg: theme.tertiaryBackgroundColor,
            itemActiveBg: theme.tertiaryBackgroundColor,
            itemSelectedBg: theme.selectedListItemBackground,
          }
        : {},
      Select: {
        ...inputControl,
        ...inputIcons,
        // The clear button sits on the input; antd fills it with the page background.
        colorBgBase: theme.inputBackgroundColor,
        // Dark options and tags take the app's menu highlight rather than antd's translucent
        // fills.
        ...(isDark && {
          optionActiveBg: theme.activeMenuItemBackgroundColor,
          optionSelectedBg: theme.activeMenuItemBackgroundColor,
          multipleItemBg: theme.activeMenuItemBackgroundColor,
          multipleItemBorderColor: theme.tertiaryBackgroundColor,
        }),
      },
      Table: {
        // antd 4's corners and 500-weight headers with no dividers, kept with the corner radii
        // (docs/antd5-migration-plan.md, 9.1).
        ...ANTD4_RADIUS,
        fontWeightStrong: 500,
        headerSplitColor: 'transparent',
        headerBorderRadius: 0,
        // Tables use Horizon's surface steps: a tertiary header, hover, expanded and sorted
        // cells, and the accent fill for selected rows.
        headerBg: theme.tertiaryBackgroundColor,
        headerColor: theme.labelColor,
        colorIcon: theme.tableHeaderButtonColor,
        colorIconHover: theme.labelColor,
        headerFilterHoverBg: isDark ? theme.secondaryBackgroundColor : theme.tableAccentColor,
        rowHoverBg: theme.tertiaryBackgroundColor,
        rowExpandedBg: theme.tertiaryBackgroundColor,
        rowSelectedBg: theme.tableAccentColor,
        rowSelectedHoverBg: theme.tableAccentColor,
        bodySortBg: theme.tertiaryBackgroundColor,
        footerBg: theme.secondaryBackgroundColor,
        expandIconBg: theme.buttonBackgroundColor,
        ...(isDark && {
          headerSortActiveBg: theme.secondaryBackgroundColor,
          headerSortHoverBg: theme.secondaryBackgroundColor,
          fixedHeaderSortActiveBg: theme.secondaryBackgroundColor,
          borderColor: `${theme.borderColor}8a`,
        }),
      },
      // The unchecked track follows the disabled text color, which is faint on the dark theme's
      // navy.
      Switch: isDark ? { colorTextQuaternary: theme.borderColor } : {},
      // antd's default rail is a 4% (light) or 8% (dark) text-color fill, faint on both
      // themes, and its disabled track is fainter still.
      Slider: {
        railBg: theme.borderColor,
        railHoverBg: theme.borderColor,
        trackBg: theme.teal,
        trackHoverBg: theme.teal,
        trackBgDisabled: theme.secondaryTextColor,
        handleColor: theme.teal,
        colorPrimaryBorderHover: theme.teal,
        dotActiveBorderColor: theme.teal,
      },
      // Space.Addon, the InputNumber addon's replacement, draws with these global tokens; match
      // the input beside it and the button-colored fill the old addonBg gave it.
      Space: {
        ...inputControl,
        colorBgContainerDisabled: theme.buttonBackgroundColor,
      },
      Tabs: {
        colorBorderSecondary: theme.borderColor,
        controlItemBgHover: theme.activeMenuItemBackgroundColor,
        itemSelectedColor: theme.actionText,
        itemHoverColor: theme.actionText,
        itemActiveColor: theme.actionTextHover,
      },
      // Uncolored dark tags take the app's menu highlight; colored tags keep their own fill.
      Tag: isDark
        ? {
            defaultBg: theme.activeMenuItemBackgroundColor,
            defaultColor: theme.secondaryTextColor,
            colorBorder: theme.tertiaryBackgroundColor,
          }
        : {},
      TreeSelect: {
        ...inputControl,
        ...inputIcons,
      },
    },
  };
}
